import React from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import { cssMap } from '@atlaskit/css';
import Field from '@atlaskit/form/field';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

export const messages: {
	sharingChangeToPersonalWarning: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	saveToPersonal: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	saveToSiteWide: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	saveToPersonalTooltip: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	saveToSiteWideTooltip: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	sharingChangeToPersonalWarning: {
		id: 'snippets.snippetEditorModal.sharingChangeToPersonalWarning',
		defaultMessage: 'If you change this to a personal template it will only be available to you',
		description: 'Warning shown when a previously shared template is changed to personal',
	},
	saveToPersonal: {
		id: 'snippets.snippetEditorModal.saveToPersonal',
		defaultMessage: 'Personal',
		description: 'Personal save option for block templates',
	},
	saveToSiteWide: {
		id: 'snippets.snippetEditorModal.saveToSiteWide',
		defaultMessage: 'Site wide',
		description: 'Site wide save option for block templates',
	},
	saveToPersonalTooltip: {
		id: 'snippets.snippetEditorModal.saveToPersonalTooltip',
		defaultMessage: 'Save to this space to keep this template private.',
		description:
			'The text is shown in a tooltip when the user hovers over the "Personal" sharing option button in the block template Create or Edit modal. It explains that saving to this space keeps the template private.',
	},
	saveToSiteWideTooltip: {
		id: 'snippets.snippetEditorModal.saveToSiteWideTooltip',
		defaultMessage: 'Site wide lets your company access this template but not edit.',
		description:
			'The text is shown in a tooltip when the user hovers over the "Site wide" sharing option button in the block template Create or Edit modal. It explains that site-wide sharing lets all company members access (but not edit) the template.',
	},
});

const styles = cssMap({
	saveToButtonGroup: {
		display: 'flex',
		width: '100%',
		gap: token('space.050'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		padding: token('space.025'),
	},
	saveToButtonSlot: {
		flex: '1 1 0',
	},
	warningIconWrapper: {
		display: 'flex',
		marginTop: token('space.025'),
	},
});

interface SnippetSharingFieldProps {
	name: string;
	label: React.ReactNode;
	defaultValue: boolean | undefined;
}

export const SnippetSharingField: FC<SnippetSharingFieldProps> = ({
	name,
	label,
	defaultValue,
}) => {
	const intl = useIntl();

	return (
		<Field<boolean> name={name} label={label} defaultValue={defaultValue}>
			{({ fieldProps }) => (
				<Stack space="space.075">
					<Inline
						role="group"
						aria-labelledby={fieldProps['aria-labelledby']}
						space="space.050"
						xcss={styles.saveToButtonGroup}
					>
						<Box xcss={styles.saveToButtonSlot}>
							<Tooltip
								content={intl.formatMessage(messages.saveToPersonalTooltip)}
								position="bottom"
							>
								<Button
									appearance="subtle"
									isSelected={fieldProps.value === false}
									onClick={() => fieldProps.onChange(false)}
									aria-pressed={fieldProps.value === false}
									shouldFitContainer
								>
									<FormattedMessage {...messages.saveToPersonal} />
								</Button>
							</Tooltip>
						</Box>
						<Box xcss={styles.saveToButtonSlot}>
							<Tooltip
								content={intl.formatMessage(messages.saveToSiteWideTooltip)}
								position="bottom"
							>
								<Button
									appearance="subtle"
									isSelected={fieldProps.value === true}
									onClick={() => fieldProps.onChange(true)}
									aria-pressed={fieldProps.value === true}
									shouldFitContainer
								>
									<FormattedMessage {...messages.saveToSiteWide} />
								</Button>
							</Tooltip>
						</Box>
					</Inline>
					{defaultValue === true && fieldProps.value === false && (
						<Inline space="space.075">
							<Box xcss={styles.warningIconWrapper}>
								<WarningIcon label="warning" color={token('color.icon.warning')} size="small" />
							</Box>
							<Text size="small" color="color.text.warning">
								<FormattedMessage {...messages.sharingChangeToPersonalWarning} />
							</Text>
						</Inline>
					)}
				</Stack>
			)}
		</Field>
	);
};
